<template>
  <div class="practice-products-content">
    <Loading :loading="loading" loadingText="Loading...">
    <div class="products-content-wrapper" :class="{ 'loading': loading }">
    <!-- Filter Row -->
    <div class="filter-row">
      <div v-if="providerOptions.length > 0" class="filter-item">
        <label class="filter-label">Provider</label>
        <SingleSelector
          v-model="filterProvider"
          size="medium"
          placeholder="Select"
          :options="providerOptions"
        />
      </div>
      <div class="filter-item">
        <label class="filter-label">Sort By</label>
        <SingleSelector
          v-model="sortByValue"
          size="medium"
          placeholder="Select"
          :options="sortByOptions"
        />
      </div>
      <div class="filter-item">
        <label class="filter-label">Product Type</label>
        <SingleSelector
          v-model="productType"
          size="medium"
          placeholder="Select"
          :options="productTypeOptions"
        />
      </div>
      <ResponsiveButton
        class="add-new-btn-wrapper"
        button_type="filled"
        built_in_theme="dark"
        size="medium"
        display_name="Add New Product"
        @click-button="showAddDialog = true"
      >
        <template #prefix>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M12.6667 8.66667H8.66671V12.6667H7.33337V8.66667H3.33337V7.33333H7.33337V3.33333H8.66671V7.33333H12.6667V8.66667Z" fill="white"/>
          </svg>
        </template>
      </ResponsiveButton>
    </div>

    <!-- Card Grid -->
    <div v-if="pagedProducts.length > 0" class="card-grid">
      <PracticeProductCard
        v-for="product in pagedProducts"
        :key="product.id"
        :product="product"
        :currency-symbol="currencySymbol"
        :show-assignees="isClinic"
        :sold-info="merchSoldInfo[product.id] || null"
        @toggle-status="handleToggleStatus"
        @delete-product="handleDeleteProduct"
        @edit-product="handleEditProduct"
      />
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state">
      <p class="empty-title">No practice products found</p>
      <p class="empty-subtitle">Create a new product to get started.</p>
    </div>

    <!-- Pagination -->
    <Pagination
      v-if="filteredSortedProducts.length > 0"
      class="pp-pagination"
      :total_items="totalCount"
      :current_page="currentPage"
      :per_page="pageSize"
      :per_page_options="pageSizeOptions"
      @update:current_page="handlePageChange"
      @update:per_page="handlePageSizeChange"
    />
    </div> <!-- End products-content-wrapper -->
    </Loading>

    <DeleteProductDialog
      v-model="showDeleteDialog"
      :product="deleteTarget"
      :merch-payload="merchPayload"
      @deleted="handleDeleteDone"
    />

    <AddProductDialog
      v-model="showAddDialog"
      :merch-payload="merchPayload"
      :is-clinic="isClinic"
      :account-id="merchPayload.account_id"
      :provider-options="providerOptions"
      @created="handleProductCreated"
    />

    <EditProductDialog
      v-model="showEditDialog"
      :product="editTarget"
      :merch-payload="merchPayload"
      :is-clinic="isClinic"
      :account-id="merchPayload.account_id"
      :provider-options="providerOptions"
      @updated="handleProductUpdated"
    />
  </div>
</template>

<script>
import PracticeProductCard from './PracticeProductCard.vue';
import DeleteProductDialog from './DeleteProductDialog.vue';
import AddProductDialog from './AddProductDialog.vue';
import EditProductDialog from './EditProductDialog.vue';
import { getAllMerchandises, merchandiseOrderInfo, editMerchandise } from '@/api/markup';
import Loading from '@/NewLibrary/Loading/Loading.vue';

export default {
  name: 'PracticeProducts',
  components: {
    PracticeProductCard,
    DeleteProductDialog,
    AddProductDialog,
    EditProductDialog,
    Loading,
  },
  props: {
    providerOptions: {
      type: Array,
      default: () => [],
    },
    currencySymbol: {
      type: String,
      default: '$',
    },
    isClinic: {
      type: Boolean,
      default: false,
    },
    searchQuery: {
      type: String,
      default: '',
    },
    userInfo: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      filterProvider: '',
      sortByValue: 'default',
      productType: 'all',
      currentPage: 1,
      pageSize: 10,
      pageSizeOptions: [10, 20, 30],

      sortByOptions: [
        // { label: 'Last Modified', value: 'update_descending' },
        // { label: 'Oldest Modified', value: 'update_ascending' },
        // { label: 'Created (Latest)', value: 'id_descending' },
        // { label: 'Created (Oldest)', value: 'id_ascending' },
        // { label: 'Product Name A-Z', value: 'name_ascending' },
        // { label: 'Product Name Z-A', value: 'name_descending' },
        // { label: 'Price High to Low', value: 'price_descending' },
        // { label: 'Price Low to High', value: 'price_ascending' },
        { label: 'Recommendation', value: 'default' },
        { label: 'Name A-Z', value: 'nameAZ' },
        { label: 'Name Z-A', value: 'nameZA' },
        { label: 'Price Highest to Lowest', value: 'price_descending' },
        { label: 'Price Lowest to Highest', value: 'price_ascending' },
      ],
      productTypeOptions: [
        { label: 'All', value: 'all' },
        { label: 'Item', value: 'item' },
        { label: 'Service', value: 'service' },
      ],

      // Raw merch data from API keyed by affiliate ID (same as VA Portal merches state)
      rawMerches: {},
      // Formatted merch data keyed by affiliate ID (same as VA Portal merchMap getter)
      merchMap: {},
      // Sold/revenue info keyed by merch id (same as VA Portal merchSoldInfo)
      merchSoldInfo: {},

      showDeleteDialog: false,
      deleteTarget: null,
      showAddDialog: false,
      showEditDialog: false,
      editTarget: null,
    };
  },

  computed: {
    accountType() {
      return this.isClinic ? 'clinic' : 'customer';
    },

    merchPayload() {
      return {
        account_type: this.accountType,
        customer_id: this.userInfo?.customer_id || '',
        clinic_id: this.userInfo?.clinic_id || '',
        account_id: this.isClinic
          ? this.userInfo?.clinic_id || ''
          : this.userInfo?.customer_id || '',
      };
    },

    currentData() {
      if (!this.merchMap || !this.filterProvider || !this.merchMap[this.filterProvider]) {
        return [];
      }
      return this.merchMap[this.filterProvider];
    },

    filteredSortedProducts() {
      return this.filterAndSortData(this.currentData, {
        searchInput: this.searchQuery,
        sortByValue: this.sortByValue,
        productType: this.productType,
      });
    },

    totalCount() {
      return this.filteredSortedProducts.length;
    },

    pagedProducts() {
      const start = this.pageSize * (this.currentPage - 1);
      return this.filteredSortedProducts.slice(start, start + this.pageSize);
    },

    merchIdList() {
      const uniqueIds = new Set();
      Object.values(this.rawMerches).forEach(merchArray => {
        if (Array.isArray(merchArray)) {
          merchArray.forEach(ele => {
            if (ele.id) uniqueIds.add(ele.id);
          });
        }
      });
      return Array.from(uniqueIds);
    },

  },

  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    productType() {
      this.currentPage = 1;
    },
    sortByValue() {
      this.currentPage = 1;
    },
    pageSize() {
      this.currentPage = 1;
    },
    providerOptions: {
      immediate: true,
      handler(options) {
        if (options.length > 0 && !this.filterProvider) {
          this.filterProvider = options[0].value;
        }
      },
    },
    merchIdList(newVal) {
      if (!newVal || newVal.length === 0) {
        this.merchSoldInfo = {};
      } else {
        this.fetchMerchSoldInfo();
      }
    },
  },

  async mounted() {
    await this.fetchData();
  },

  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const { data } = await getAllMerchandises(this.merchPayload);
        if (data) {
          this.rawMerches = data;
          this.merchMap = this.formatDataForFrontendTable(data);
        }
      } catch (error) {
        console.error('Error fetching merchandises:', error);
      } finally {
        this.loading = false;
      }
    },

    async fetchMerchSoldInfo() {
      try {
        const { data } = await merchandiseOrderInfo({ item_ids: this.merchIdList });
        if (data) {
          this.merchSoldInfo = data;
        }
      } catch (error) {
        console.error('Error fetching merch sold info:', error);
      }
    },

    // Same as VA Portal formatDataForFrontendTable
    formatDataForFrontendTable(data) {
      const res = {};
      for (const key in data) {
        if (Array.isArray(data[key])) {
          res[key] = data[key].map((obj, idx) => this.parseObjectFromApi(obj, idx, key));
        }
      }
      return res;
    },

    // Same as VA Portal parseObjectFromApi (simplified for management page, no order context)
    parseObjectFromApi(obj, index, key = null) {
      console.log('parseObjectFromApi - raw obj keys:', Object.keys(obj));
      console.log('parseObjectFromApi - description value:', obj.description);
      console.log('parseObjectFromApi - full obj:', obj);

      const {
        id,
        name,
        type,
        description,
        currency,
        price,
        picture,
        return_and_refund_info,
        updated_at,
        status,
        creator_type,
        apply_to_self,
        apply_to_all_providers,
        apply_merchandise_info_to,
      } = obj;

      const currencySign = this.currencySymbol;
      const formattedPrice = currencySign + price.toFixed(2);
      const isAll = apply_to_all_providers;
      const isSelf = apply_to_self;
      let belongToProvider = null;
      if ((isSelf || creator_type === 'provider') && key) {
        belongToProvider = key;
      }
      const providerList = apply_merchandise_info_to
        ? apply_merchandise_info_to
        : isSelf
          ? [belongToProvider]
          : [];

      // Determine returnDays
      let returnDaysFormatted = null;
      let returnDaysNum = null;
      if (return_and_refund_info && return_and_refund_info.accept_return_refund) {
        returnDaysNum = return_and_refund_info.accepted_return_refund_days;
        returnDaysFormatted = returnDaysNum > 1 ? `${returnDaysNum} Days` : `${returnDaysNum} Day`;
      }

      // Determine refundPolicy
      let refundPolicy = null;
      if (return_and_refund_info && return_and_refund_info.accept_return_refund) {
        const deductionType = return_and_refund_info.merchandise_refund_deduction_type;
        const deductionValue = return_and_refund_info.merchandise_refund_deduction_value;
        if (deductionType === 'no_deduction') {
          refundPolicy = '100% Full';
        } else if (deductionType === 'by_percentage') {
          const percentage = (1 - deductionValue) * 100;
          const percentageStr = Number.isInteger(percentage) ? `${percentage}%` : `${percentage.toFixed(1)}%`;
          refundPolicy = percentageStr + ' Partial';
        } else if (deductionType === 'by_constant') {
          refundPolicy = currencySign + Math.max((price - deductionValue), 0).toFixed(2) + ' Partial';
        }
      }

      const result = {
        index,
        id,
        update: updated_at,
        name,
        type,
        description,
        currency,
        price: formattedPrice,
        originalPrice: price,
        image: picture,
        return_and_refund_info,
        returnDays: returnDaysFormatted,
        returnDaysNum,
        refundPolicy,
        status,
        creator_type,
        isAll,
        isSelf,
        providerList,
        belongToProvider,
      };

      console.log('parseObjectFromApi - result.description:', result.description);
      console.log('parseObjectFromApi - result:', result);

      return result;
    },

    handlePageChange(page) {
      this.currentPage = page;
    },
    handlePageSizeChange(size) {
      this.pageSize = size;
      this.currentPage = 1;
    },

    async handleToggleStatus(index, action) {
      const merch = this.rawMerches[this.filterProvider]?.[index];
      if (!merch) return;

      const payload = {
        ...this.merchPayload,
        id: merch.id,
      };

      try {
        await editMerchandise(action, payload);
        // Update local status immediately
        this.rawMerches[this.filterProvider][index].status = action === 'activate' ? 'active' : 'inactive';
        // Re-format to update the UI
        this.merchMap = this.formatDataForFrontendTable(this.rawMerches);
      } catch (error) {
        console.error('Error toggling merchandise status:', error);
      }
    },

    handleDeleteProduct(product) {
      this.deleteTarget = product;
      this.showDeleteDialog = true;
    },

    async handleProductCreated() {
      // Re-fetch all products to get the newly created one
      await this.fetchData();
    },

    handleEditProduct(product) {
      console.log('handleEditProduct - received product:', product);
      console.log('handleEditProduct - product.description:', product.description);
      this.editTarget = product;
      console.log('handleEditProduct - editTarget.description:', this.editTarget.description);
      // Use nextTick to ensure editTarget is set before opening dialog
      this.$nextTick(() => {
        this.showEditDialog = true;
      });
    },

    async handleProductUpdated() {
      // Re-fetch all products to get the updated one
      await this.fetchData();
    },

    handleDeleteDone(product) {
      // Remove from rawMerches
      const arr = this.rawMerches[this.filterProvider];
      const idx = arr.findIndex(m => m.id === product.id);
      if (idx !== -1) {
        arr.splice(idx, 1);
      }
      // Re-format to update the UI
      this.merchMap = this.formatDataForFrontendTable(this.rawMerches);
      this.deleteTarget = null;
    },

    // Same as VA Portal filterAndSortData
    filterAndSortData(array, params) {
      const { searchInput, sortByValue, productType } = params;
      let filteredArray = [...array];

      // Step 1: Filter by product type
      if (productType && productType !== 'all') {
        filteredArray = filteredArray.filter(item =>
          item.type && item.type.toLowerCase() === productType.toLowerCase()
        );
      }

      // Step 2: Filter by search
      if (searchInput && searchInput.trim() !== '') {
        const lowered = searchInput.trim().toLowerCase();
        filteredArray = filteredArray.filter(item =>
          item.name.toLowerCase().includes(lowered)
        );
      }

      // Step 2: Sort
      if (sortByValue && sortByValue !== 'default') {
        if (sortByValue === 'nameAZ') {
          filteredArray.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        } else if (sortByValue === 'nameZA') {
          filteredArray.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
        } else {
          const [key, order] = sortByValue.split('_');

          filteredArray.sort((a, b) => {
            let valA = a[key];
            let valB = b[key];

            if (key === 'price') {
              valA = parseFloat(String(valA).replace(/[^0-9.-]+/g, '')) || 0;
              valB = parseFloat(String(valB).replace(/[^0-9.-]+/g, '')) || 0;
            // } else if (key === 'update') {
            //   valA = new Date(valA);
            //   valB = new Date(valB);
            }

            if (valA < valB) return order === 'ascending' ? -1 : 1;
            if (valA > valB) return order === 'ascending' ? 1 : -1;
            return 0;
          });
        }
      }

      return filteredArray;
    },
  },
};
</script>

<style scoped>
.practice-products-content {
  padding: 0 26px;
  position: relative;
  min-height: 500px;
}

/* Filter Row */
.filter-row {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  padding-top: 20px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 250px;
}

.filter-label {
  overflow: hidden;
  color: var(--color-Grey-Grey-4A, #4A4A4A);
  text-overflow: ellipsis;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
}

/* Push the Add New Product button to the right of the filter row */
.add-new-btn-wrapper {
  margin-left: auto;
}

/* Breathing space between the card grid and the Pagination */
.pp-pagination {
  margin-top: 16px;
}

.products-content-wrapper.loading {
  opacity: 0.3;
  pointer-events: none;
}

/* Card Grid */
.card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 24px;
  padding: 8px 0 16px;
  max-height: 555px;
  overflow-y: auto;
}

.card-grid::-webkit-scrollbar {
  width: 6px;
}

.card-grid::-webkit-scrollbar-track {
  background: transparent;
}

.card-grid::-webkit-scrollbar-thumb {
  background: #C4C4C4;
  border-radius: 3px;
}

.card-grid::-webkit-scrollbar-thumb:hover {
  background: #999;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  border: 1px solid #E6E6E6;
  border-radius: 4px;
  margin-top: 16px;
}

.empty-title {
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: #000;
  margin: 0 0 8px;
}

.empty-subtitle {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #979797;
  margin: 0;
}

</style>
